import { useEffect, useRef, useState, useCallback } from 'react';

interface WebFaceReconSDKProps {
    sessionId: string;
    clientThemeSettings: any;
    showBackButton?: boolean;
    getToken: () => Promise<string | null>;
    onSuccess: (imageKey: string) => void;
    onUserCanceled?: () => void;
    onError?: (status: number) => void;
}

const resolveThemeConfig = (clientThemeSettings: any) => {
    if (clientThemeSettings?.palette?.primary) {
        return {
            primaryColor: clientThemeSettings.palette.primary,
            secondaryColor: clientThemeSettings.palette.secondary,
            tertiaryColor: clientThemeSettings.palette.tertiary,
        };
    }
    return {
        buttonColor: clientThemeSettings?.button_color,
        fontColor: clientThemeSettings?.font_color,
        backgroundColor: clientThemeSettings?.background_color,
    };
};

export const WebFaceReconSDK = ({
    sessionId,
    clientThemeSettings,
    showBackButton = false,
    getToken,
    onSuccess,
    onUserCanceled,
    onError,
}: WebFaceReconSDKProps) => {
    const instanceRef = useRef<any>(null);
    const [isSdkReady, setIsSdkReady] = useState(false);
    const [isTokenNeeded, setIsTokenNeeded] = useState(false);

    const stableOnSuccess = useCallback(onSuccess, []); // eslint-disable-line react-hooks/exhaustive-deps
    const stableOnUserCanceled = useCallback(() => onUserCanceled?.(), []); // eslint-disable-line react-hooks/exhaustive-deps
    const stableOnError = useCallback((s: number) => onError?.(s), []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const themeConfig = resolveThemeConfig(clientThemeSettings);

        const builder = new (window as any).QITechWebFaceRecon.WebFaceRecon()
            .setLogLevel('debug')
            .setSessionId(sessionId)
            .setThemeConfiguration(themeConfig)
            .setShowInvalidTokenScreen(true)
            .setShowBackButton(showBackButton)
            .setClientApp("qi_sign_app");

        if (process.env.REACT_APP_ENV !== 'production') {
            builder.setSandboxEnvironment();
        }

        instanceRef.current = builder.build();
        instanceRef.current.initialize()
            .then(() => {
                setIsSdkReady(true);
                setIsTokenNeeded(true);
            })
            .catch((err: any) => {
                console.error('[WebFaceReconSDK] initialize error:', err);
                if (err?.reason === 'UNSUPPORTED_BROWSER' && onUserCanceled) {
                    stableOnUserCanceled();
                    return;
                }
                stableOnError(500);
            });

        return () => {
            instanceRef.current?.destroyAll?.();
            instanceRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isSdkReady || !instanceRef.current || !isTokenNeeded) return;

        let cancelled = false;
        const run = async () => {
            const token = await getToken();
            if (cancelled || !token) return;
            setIsTokenNeeded(false);
            instanceRef.current.open(token)
                .then((result: any) => {
                    instanceRef.current?.destroyAll?.();
                    stableOnSuccess(result.data);
                })
                .catch((err: any) => {
                    instanceRef.current?.destroyAll?.();
                    if (err?.reason === 'INVALID_TOKEN') {
                        stableOnError(401);
                    } else if (err?.reason === 'USER_CANCELED') {
                        stableOnUserCanceled();
                    } else {
                        stableOnError(500);
                    }
                });
        };
        run();
        return () => { cancelled = true; };
    }, [isSdkReady, isTokenNeeded, getToken, stableOnSuccess, stableOnUserCanceled, stableOnError]);

    return null;
};
